/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import { PolicyData } from '../../auth/core/_models'
import { toAbsoluteUrl } from '../../../../_metronic/helpers'

type Props = {
  className: string
  color: string
  PolicyData: PolicyData | null
}

const getIcon = (relation: string, gender: string): string => {
  const relationMap: { [key: string]: string } = {
    self_male: 'employee_male.png',
    self_female: 'employee_female.png',
    father: 'father.png',
    mother: 'mother.png',
    son: 'son_kid.png',
    wife: 'wife.png',
    husband: 'husband.png',
    'mother in law': 'mother.png',
    'father in law': 'father.png',
    daughter: 'daughter_kid.png',
  };

  const key = relation.toLowerCase().includes('self')
    ? `self_${gender.toLowerCase()}`
    : relation.toLowerCase();

  return relationMap[key] || '';
};

const formatCurrency = (value: number | string | undefined): string => {
  if (!value) return '₹0';
  return `₹${Number(value).toLocaleString('en-IN')}`;
};

const formatCurrencyIfNumber = (value: string | undefined): string => {
  if (!value) return '₹0';
  return isNaN(Number(value)) ? value : `₹${Number(value).toLocaleString('en-IN')}`;
};

const FamilyDetailsListWidget: React.FC<Props> = ({ className, color, PolicyData }) => {
  const familyDetails = PolicyData?.policysummary.flatMap(summary => summary.employeedtl) || [];
  const hasTopUp = PolicyData?.policysummary.some(summary => summary.topUp === 'Yes');
  const hasFlexi = PolicyData?.policysummary.some(summary => summary.flexi === 'Yes');
  const hasTotalPremium = (PolicyData?.totPremium || 0) > 0;

  const showSumInsuredColumn = hasTopUp || hasFlexi;

  return (
    <div className='table-responsive'>
      {/* Family Details Table */}
      <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
        <thead>
          <tr className='fw-bolder'>
            <th className='min-w-150px'>Name</th>
            <th className='min-w-100px'>Relation</th>
            <th className='min-w-100px'>Gender</th>
            <th className='min-w-100px'>DOB</th>
            <th className='min-w-100px'>Sum Insured</th>
          </tr>
        </thead>
        <tbody>
          {familyDetails.map((family, index) => {
            const icon = getIcon(family.Relation, family.Gender);
            return (
              <tr key={index} style={{ textTransform: 'capitalize', color: 'black' }}>
                <td>
                  <div className='d-flex align-items-center'>
                    <div className='symbol symbol-50px me-5'>
                      <img src={toAbsoluteUrl('/media/family/png/' + icon)} alt='family' />
                    </div>
                    <div className='d-flex justify-content-start flex-column'>
                      <a className='fw-bolder text-hover-primary fs-6' style={{ color: 'black' }}>
                        {family.Name}
                      </a>
                    </div>
                  </div>
                </td>
                <td>
                  <a className='fw-bolder text-hover-primary d-block fs-6' style={{ color: 'black' }}>
                    {family.Relation}
                  </a>
                </td>
                <td>
                  <a className='fw-bolder text-hover-primary d-block fs-6' style={{ color: 'black' }}>
                    {family.Gender}
                  </a>
                </td>
                <td>
                  <a className='fw-bolder text-hover-primary d-block fs-6' style={{ color: 'black' }}>
                    {family.DOBtxt}
                  </a>
                </td>
                <td>
                  <a className='fw-bolder text-hover-primary d-block fs-6' style={{ color: 'black' }}>
                    {formatCurrencyIfNumber(family.SumInsuredTxt)}
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Premium Details Table */}
      {(hasTopUp || hasFlexi || hasTotalPremium) && (
        <div className='mt-5'>
          <h5 className='fw-bold'>Premium Details</h5>
          <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
            <thead>
              <tr className='fw-bolder'>
                <th className='min-w-150px'>Type</th>
                {showSumInsuredColumn && <th className='min-w-100px'>Sum Insured</th>}
                <th className='min-w-100px'>Premium</th>
                <th className='min-w-100px'>GST</th>
                <th className='min-w-100px'>Total</th>
              </tr>
            </thead>
            <tbody>
              {hasTopUp &&
                PolicyData?.topupsummary.map((topUp, index) => (
                  <tr key={`topup-${index}`} style={{ color: 'black' }}>
                    <td>Top-Up</td>
                    {showSumInsuredColumn && <td>{formatCurrency(topUp.TopUp_SumInsured)}</td>}
                    <td>{formatCurrency(topUp.TopUpPremiumE)}</td>
                    <td>{formatCurrency(topUp.TopUpGSTE)}</td>
                    <td>{formatCurrency(topUp.TopUpTotal)}</td>
                  </tr>
                ))}
              {hasFlexi &&
                PolicyData?.flexisummary.map((flexi, index) => (
                  <tr key={`flexi-${index}`} style={{ color: 'black' }}>
                    <td>Flexi</td>
                    {showSumInsuredColumn && <td>{formatCurrency(flexi.Flexi_SumInsured)}</td>}
                    <td>{formatCurrency(flexi.FlexiPremiumE)}</td>
                    <td>{formatCurrency(flexi.FlexiGSTE)}</td>
                    <td>{formatCurrency(flexi.FlexiTotal)}</td>
                  </tr>
                ))}
              {hasTotalPremium && (
                <tr className='fw-bold' style={{ color: 'black' }}>
                  <td colSpan={showSumInsuredColumn ? 2 : 1}>Total Premium</td>
                  <td>{formatCurrency(PolicyData?.totPremium)}</td>
                  <td>{formatCurrency(PolicyData?.totGST)}</td>
                  <td>{formatCurrency((PolicyData?.totPremium || 0) + (PolicyData?.totGST || 0))}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export { FamilyDetailsListWidget }
